"use client";

import { Content, asLink } from "@prismicio/client";
import { PrismicNextImage } from "@prismicio/next";
import { SliceComponentProps } from "@prismicio/react";
import * as S from "./index.styles";
import { useMediaQuery, useOnClickOutside } from "usehooks-ts";
import MenuIcon from "../../lib/MenuIcon";
import { useEffect, useRef, useState } from "react";
import TrainIcon from "../../lib/TrainIcon";
import CloseIcon from "../../lib/CloseIcon";
import { useRouter } from "next/navigation";
import { redirect } from "../../lib/redirect";

/**
 * Props for `Navigation`.
 */
export type NavigationProps = SliceComponentProps<Content.NavigationSlice>;

/**
 * Component for "Navigation" Slices.
 */
const Navigation = ({ slice }: NavigationProps): JSX.Element => {
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dialog, setDialog] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const isFixedSpace = slice.variation !== "default";
  const router = useRouter();
  const shouldShowClosedRoutes =
    slice.primary.shouldshowdropdownmenus === undefined ||
    slice.primary.shouldshowdropdownmenus === null
      ? true
      : slice.primary.shouldshowdropdownmenus;

  let isMexicoPage: boolean = false;

  if (typeof window !== "undefined") {
    // const domain = location?.host?.split(".")[location?.host?.split(".").length - 1];
    // isMexicoPage = domain === "mx";
    isMexicoPage = true;
  }

  const getDropdownUrl = (item: { link: any; hash_id?: string | null }) => {
    const baseUrl = asLink(item.link) || "";
    if (item.hash_id) {
      return baseUrl + "#" + item.hash_id;
    }
    return baseUrl;
  };

  // Check if dropdown_options exists (only in default variation)
  const dropdownOptions = (slice.primary as any).dropdown_options || [];

  useOnClickOutside(dialogRef, () => setDialog(false));

  useEffect(() => {
    redirect();
  }, []);

  if (isMobile) {
    return (
      <>
        {isFixedSpace ? (
          <S.FixedContainer $isPositive={slice.variation === "positive"}>
            <div onClick={() => setIsModalOpen(true)}>
              <MenuIcon />
            </div>
            <PrismicNextImage
              onClick={() => router.push("/")}
              field={slice.primary.logo}
              alt=""
            />
          </S.FixedContainer>
        ) : (
          <S.MobileContainer>
            <div onClick={() => setIsModalOpen(true)}>
              <MenuIcon />
            </div>
            <PrismicNextImage
              onClick={() => router.push("/")}
              field={slice.primary.logo}
              alt=""
            />
          </S.MobileContainer>
        )}
        {isModalOpen && (
          <S.ModalContainer>
            <S.TopSection>
              <TrainIcon />
              <div onClick={() => setIsModalOpen(false)}>
                <CloseIcon />
              </div>
            </S.TopSection>
            <S.MobileLink field={slice.primary.page_1}>Inicio</S.MobileLink>
            <S.DialogContainer>
              <S.FakeLink
                $isMobile
                $isPositive={slice.variation === "positive"}
                onClick={() => setDialog(!dialog)}
              >
                Rutas
              </S.FakeLink>
              {dialog && (
                <S.DialogWrapper $isMobile>
                  {dropdownOptions.length > 0 ? (
                    dropdownOptions.map((item: any, index: number) => (
                      <S.LinkItem
                        key={index}
                        href={getDropdownUrl(item)}
                        $isBold={item.is_bold}
                      >
                        {item.label}
                      </S.LinkItem>
                    ))
                  ) : (
                    <>
                      {isMexicoPage ? (
                        <S.LinkTitle href="/todas-las-rutas">
                          Ver todo
                        </S.LinkTitle>
                      ) : (
                        <S.LinkTitle href="/rutas">Ver todo</S.LinkTitle>
                      )}
                      {shouldShowClosedRoutes &&
                        (isMexicoPage ? (
                          <S.LinkItem href={"https://mundorail.ebeweb.com.mx"}>
                            Reservas de contratación directa
                          </S.LinkItem>
                        ) : (
                          <S.LinkItem href="/rutas#garantizadas">
                            Reservas de contratación directa
                          </S.LinkItem>
                        ))}
                      <S.LinkItem
                        href={isMexicoPage ? "/#flexibles" : "/rutas#flexibles"}
                      >
                        Reservas a medida
                      </S.LinkItem>
                    </>
                  )}
                </S.DialogWrapper>
              )}
            </S.DialogContainer>
            <S.MobileLink field={slice.primary.page_3}>Nosotros</S.MobileLink>
            <S.MobileLink field={slice.primary.page_4}>Contacto</S.MobileLink>
          </S.ModalContainer>
        )}
      </>
    );
  }

  if (isFixedSpace) {
    return (
      <S.FixedNavigationContainer
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        $isPositive={slice.variation === "positive"}
      >
        <S.InternalContainer>
          <S.Link
            $isPositive={slice.variation === "positive"}
            field={slice.primary.page_1}
          >
            Inicio
          </S.Link>
          <S.DialogContainer>
            <S.FakeLink
              $isPositive={slice.variation === "positive"}
              onClick={() => setDialog(!dialog)}
            >
              Rutas
            </S.FakeLink>
            {dialog && (
              <S.DialogWrapper>
                {dropdownOptions.length > 0 ? (
                  dropdownOptions.map((item: any, index: number) => (
                    <S.LinkItem
                      key={index}
                      href={getDropdownUrl(item)}
                      $isBold={item.is_bold}
                    >
                      {item.label}
                    </S.LinkItem>
                  ))
                ) : (
                  <>
                    {isMexicoPage ? (
                      <S.LinkTitle href="/todas-las-rutas">
                        Ver todo
                      </S.LinkTitle>
                    ) : (
                      <S.LinkTitle href="/rutas">Ver todo</S.LinkTitle>
                    )}
                    {shouldShowClosedRoutes &&
                      (isMexicoPage ? (
                        <S.LinkItem href={"https://mundorail.ebeweb.com.mx"}>
                          Reservas de contratación directa
                        </S.LinkItem>
                      ) : (
                        <S.LinkItem href="/rutas#garantizadas">
                          Reservas de contratación directa
                        </S.LinkItem>
                      ))}
                    <S.LinkItem
                      href={isMexicoPage ? "/#flexibles" : "/rutas#flexibles"}
                    >
                      Reservas a medida
                    </S.LinkItem>
                  </>
                )}
              </S.DialogWrapper>
            )}
          </S.DialogContainer>
          <PrismicNextImage
            onClick={() => router.push("/")}
            field={slice.primary.logo}
            alt=""
          />
          <S.Link
            $isPositive={slice.variation === "positive"}
            field={slice.primary.page_3}
          >
            Nosotros
          </S.Link>
          <S.Link
            $isPositive={slice.variation === "positive"}
            field={slice.primary.page_4}
          >
            Contacto
          </S.Link>
        </S.InternalContainer>
      </S.FixedNavigationContainer>
    );
  }

  console.log(isMexicoPage);

  return (
    <S.NavigationContainer
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <S.Link field={slice.primary.page_1}>Inicio</S.Link>
      <S.DialogContainer>
        <S.FakeLink onClick={() => setDialog(!dialog)}>Rutas</S.FakeLink>
        {dialog && (
          <S.DialogWrapper ref={dialogRef}>
            {dropdownOptions.length > 0 ? (
              dropdownOptions.map((item: any, index: number) => (
                <S.LinkItem
                  key={index}
                  href={getDropdownUrl(item)}
                  $isBold={item.is_bold}
                >
                  {item.label}
                </S.LinkItem>
              ))
            ) : (
              <>
                {isMexicoPage ? (
                  <S.LinkTitle href="/todas-las-rutas">Ver todo</S.LinkTitle>
                ) : (
                  <S.LinkTitle href="/rutas">Ver todo</S.LinkTitle>
                )}
                {shouldShowClosedRoutes &&
                  (isMexicoPage ? (
                    <S.LinkItem href={"https://mundorail.ebeweb.com.mx"}>
                      Reservas de contratación directa
                    </S.LinkItem>
                  ) : (
                    <S.LinkItem href="/rutas#garantizadas">
                      Reservas de contratación directa
                    </S.LinkItem>
                  ))}
                <S.LinkItem
                  href={isMexicoPage ? "/#flexibles" : "/rutas#flexibles"}
                >
                  Reservas a medida
                </S.LinkItem>
              </>
            )}
          </S.DialogWrapper>
        )}
      </S.DialogContainer>
      <PrismicNextImage
        onClick={() => router.push("/")}
        field={slice.primary.logo}
        alt=""
      />
      <S.Link field={slice.primary.page_3}>Nosotros</S.Link>
      <S.Link field={slice.primary.page_4}>Contacto</S.Link>
    </S.NavigationContainer>
  );
};

export default Navigation;
